import React, { useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import OnboardingCarousel from 'components/v2/onboarding/OnboardingCarousel';
import { getOnboardingSlides } from 'components/v2/onboarding/OnboardingContent';

const DEFAULT_TITLE = 'Tutoriel MOBIUSpack';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '20px',
        maxWidth: '900px',
        width: '90vw',
        maxHeight: '85vh',
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
    },
}));

const StyledDialogTitle = styled(DialogTitle)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5em 2em 1em 2em',
    borderBottom: '1px solid #EFF0F6',
});

const TitleText = styled(Typography)({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(18px, 2vw, 24px)',
    color: '#0072E4',
});

const StyledDialogContent = styled(DialogContent)({
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
});

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme?.palette?.text?.secondary || '#666',
    '&:hover': {
        backgroundColor: '#F5F5F5',
    },
}));

const SkipButton = styled(Box)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: 'clamp(13px, 1.1vw, 15px)',
    color: theme?.palette?.text?.secondary || '#666',
    cursor: 'pointer',
    padding: '0.5em 1em',
    borderRadius: '8px',
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: '#F5F5F5',
    },
}));

/**
 * Modal principal du tutoriel d'onboarding
 * Affiche un carrousel de slides avec tracking des métriques utilisateur
 * 
 * @param {boolean} open - État d'ouverture du modal
 * @param {function} onClose - Callback appelé à la fermeture
 * @param {function} trackSlideView - Tracker l'affichage d'un slide
 * @param {function} markAsCompleted - Marquer le tutoriel comme complété
 * @param {function} saveOnClose - Sauvegarder l'état à la fermeture
 * @param {function} startTimer - Démarrer le timer de session
 * @param {function} incrementViews - Incrémenter le compteur de vues
 * @param {string[]} userRoles - Rôles de l'utilisateur : le tutoriel ne montre que
 *                               les pages auxquelles il a accès (cf. OnboardingContent)
 * @param {function} [getSlides] - Fournisseur de slides alternatif (ex: ancien tutoriel V2),
 *                                 remplace getOnboardingSlides par défaut
 * @param {string} [title] - Titre affiché dans l'en-tête de la modale
 */
const OnboardingTutorialModal = ({ open, onClose, trackSlideView, markAsCompleted, saveOnClose, startTimer, incrementViews, userRoles, getSlides = getOnboardingSlides, title = DEFAULT_TITLE }) => {
    const slides = useMemo(() => getSlides(userRoles), [getSlides, userRoles]);

    // Démarrer le timer et incrémenter les vues à l'ouverture
    useEffect(() => {
        if (open) {
            startTimer();
            incrementViews();
        }
    }, [open, startTimer, incrementViews]);

    // Tracker chaque changement de slide
    const handleStepChange = (slideIndex) => {
        trackSlideView(slideIndex);
    };

    // Gérer la complétion du tutoriel
    const handleComplete = async () => {
        await markAsCompleted();
        if (onClose) {
            onClose();
        }
    };

    // Gérer la fermeture - marquer comme complété pour ne plus afficher automatiquement
    const handleClose = async () => {
        await saveOnClose(true); // Toujours marquer comme complété, même si partiellement vu
        if (onClose) {
            onClose();
        }
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            disableEscapeKeyDown={false}
        >
            <StyledDialogTitle>
                <TitleText>
                    {title}
                </TitleText>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <SkipButton onClick={handleClose}>
                        Passer
                    </SkipButton>
                    <CloseButton onClick={handleClose} aria-label="fermer">
                        <CloseIcon />
                    </CloseButton>
                </Box>
            </StyledDialogTitle>

            <StyledDialogContent>
                <OnboardingCarousel
                    steps={slides}
                    onStepChange={handleStepChange}
                    onComplete={handleComplete}
                    onClose={handleClose}
                />
            </StyledDialogContent>
        </StyledDialog>
    );
};

export default OnboardingTutorialModal;
